/**
 * src/v2/services/authorizationService.ts
 *
 * The client for `/api/v2/authorization`. Every administrative surface asks
 * this one service rather than assembling an answer from organization,
 * spatial and core reads of its own.
 *
 * ## Coalescing, and what is deliberately not cached
 *
 * One object costs the backend roughly a dozen contract simulations, and a
 * list of twenty rows would otherwise mean twenty of those. Two things
 * prevent that: identical in-flight requests share one promise, and a
 * resolved answer is held for a few seconds so a re-render does not re-ask.
 *
 * The window is short on purpose. Authorization changes the instant a
 * binding is approved or an organization is suspended, and a stale "yes" is
 * worse than a slow "no" — so it is measured in seconds, and every write
 * path calls `invalidate` rather than waiting for expiry.
 */

import { V2_API } from '../config';
import type {
  ObjectAuthorization,
  CountryAuthorization,
  OrganizationAuthorization,
} from './authorizationModel';

const base = (path = ''): string => `${V2_API}/authorization${path}`;

/** Matches BATCH_MAX in server/v2/routes/authorization.js. */
const BATCH_MAX = 50;

/**
 * How long a resolved answer may be reused.
 *
 * Long enough to absorb a burst of renders, short enough that a permission
 * revoked in another tab stops being honoured almost immediately.
 */
const TTL_MS = 5_000;

// ── Types ────────────────────────────────────────────────────────────────────

export interface AuthorizationConfig {
  configured: boolean;
  contracts: Record<string, { configured: boolean; contractId: string | null }>;
  permissions: string[];
  authoritySources: string[];
  permissionBits: Record<string, number>;
  complianceListEnforcedOnChain: boolean;
}

export interface ObjectAuthorizationBatch {
  wallet: string;
  /** Objects the chain knows about. */
  records: ObjectAuthorization[];
  /** Valid ids the registry has never seen. Still carry country-scoped answers. */
  unregistered: ObjectAuthorization[];
  invalid: string[];
  failed: { objectId: string; error: string }[];
}

// ── Fetch plumbing ───────────────────────────────────────────────────────────

async function asJson<T>(resp: Response): Promise<T> {
  let body: unknown = null;
  try { body = await resp.json(); } catch { /* empty or non-JSON body */ }

  if (!resp.ok) {
    const err = body as { error?: string; message?: string } | null;
    throw new Error(err?.error || err?.message || `Request failed: ${resp.status}`);
  }
  return body as T;
}

async function get<T>(path: string): Promise<T> {
  return asJson<T>(await fetch(base(path)));
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return asJson<T>(await fetch(base(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }));
}

// ── Coalescing cache ─────────────────────────────────────────────────────────

interface Entry<T> {
  promise: Promise<T>;
  /** Set once resolved; absent while the request is still in flight. */
  settledAt?: number;
}

const cache = new Map<string, Entry<unknown>>();

/**
 * Run `load` unless an identical request is in flight or recently settled.
 *
 * A rejected request is evicted immediately rather than cached, so a
 * transient RPC failure does not lock a panel into an error for the whole
 * TTL.
 */
function coalesce<T>(key: string, load: () => Promise<T>): Promise<T> {
  const existing = cache.get(key) as Entry<T> | undefined;
  if (existing) {
    const fresh = existing.settledAt === undefined
      || Date.now() - existing.settledAt < TTL_MS;
    if (fresh) return existing.promise;
  }

  const entry: Entry<T> = {
    promise: load().then(
      (value) => {
        entry.settledAt = Date.now();
        return value;
      },
      (err) => {
        cache.delete(key);
        throw err;
      },
    ),
  };
  cache.set(key, entry as Entry<unknown>);
  return entry.promise;
}

/**
 * Everyone currently displaying an authorization answer.
 *
 * Dropping the cache is not enough on its own: a panel that already has an
 * answer in React state will keep showing it until something tells it to
 * ask again. Reassignment is the case that makes this matter — completing
 * one revokes every delegated grant on the object, and any panel still
 * rendering the old answer is offering buttons the chain will now refuse.
 */
const listeners = new Set<() => void>();

/** Subscribe to invalidation. Returns the unsubscribe function. */
export function onAuthorizationInvalidated(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Drop cached answers and tell every mounted consumer to ask again.
 *
 * With no argument, everything: the right move after a write whose blast
 * radius is hard to bound, such as suspending an organization.
 *
 * With an object id, that object *and every other cached object*, because
 * children inherit authority through `get_effective_org` and there is no
 * cheap way to know which cached entries are its descendants. The object id
 * is therefore documentation of intent rather than a narrowing filter.
 * Over-invalidating costs a few reads; under-invalidating leaves a revoked
 * operator with live buttons.
 */
export function invalidateAuthorization(objectId?: string): void {
  if (!objectId) {
    cache.clear();
  } else {
    for (const key of Array.from(cache.keys())) {
      if (key.startsWith('object:') || key.startsWith('batch:')) cache.delete(key);
    }
  }
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // One panel's re-read failing must not stop the others being told.
    }
  });
}

// ── Object ids ───────────────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX16_RE = /^[0-9a-f]{32}$/i;

function normalizeObjectId(value: unknown): string | null {
  const raw = String(value ?? '').trim();
  if (UUID_RE.test(raw)) return raw.replace(/-/g, '').toLowerCase();
  if (HEX16_RE.test(raw)) return raw.toLowerCase();
  return null;
}

// ── Reads ────────────────────────────────────────────────────────────────────

let configPromise: Promise<AuthorizationConfig> | null = null;

/**
 * Whether the authorization service can answer at all.
 *
 * Cached for the session: contract ids come from server environment and do
 * not change without a restart.
 */
export function getAuthorizationConfig(): Promise<AuthorizationConfig> {
  if (!configPromise) {
    configPromise = get<AuthorizationConfig>('/config').catch((err) => {
      configPromise = null;
      throw err;
    });
  }
  return configPromise;
}

/**
 * Everything one wallet may do to one object.
 *
 * Returns null only when the arguments are unusable. A request failure
 * throws, so a caller can tell "not allowed" from "could not ask" — the
 * distinction the old metadata checks could not make.
 */
export async function getObjectAuthorization(
  objectId: string,
  wallet: string,
): Promise<ObjectAuthorization | null> {
  const id = normalizeObjectId(objectId);
  if (!id || !wallet) return null;
  return coalesce(`object:${id}:${wallet}`, () =>
    get<ObjectAuthorization>(`/object/${id}/${wallet}`));
}

/**
 * The same answer for many objects, in one request per 50.
 *
 * Keyed on the whole id set, so a list that re-renders with the same rows
 * re-uses the answer and one that gains a row asks again.
 */
export async function getObjectAuthorizations(
  objectIds: string[],
  wallet: string,
): Promise<Map<string, ObjectAuthorization>> {
  const out = new Map<string, ObjectAuthorization>();
  if (!wallet) return out;

  const ids = Array.from(new Set(
    objectIds.map(normalizeObjectId).filter((x): x is string => !!x),
  ));
  if (!ids.length) return out;

  for (let i = 0; i < ids.length; i += BATCH_MAX) {
    const chunk = ids.slice(i, i + BATCH_MAX);
    const key = `batch:${wallet}:${chunk.join(',')}`;
    const data = await coalesce(key, () =>
      post<ObjectAuthorizationBatch>('/objects', { wallet, objectIds: chunk }));

    // Unregistered objects are kept: they still carry the country-scoped
    // answers, which is what tells a panel whether this wallet could
    // register the object in the first place.
    for (const record of [...(data.records ?? []), ...(data.unregistered ?? [])]) {
      if (record?.objectId) out.set(record.objectId, record);
    }
  }
  return out;
}

/**
 * Administrative standing in a country, with no object involved. This is
 * what replaces comparing a wallet to `getAdmin()` in the client.
 */
export async function getCountryAuthorization(
  countryCode: string | number,
  wallet: string,
): Promise<CountryAuthorization | null> {
  const code = String(countryCode ?? '').trim();
  if (!code || !wallet) return null;
  return coalesce(`country:${code}:${wallet}`, () =>
    get<CountryAuthorization>(`/country/${encodeURIComponent(code)}/${wallet}`));
}

/** A wallet's standing in one organization, and who may administer it. */
export async function getOrganizationAuthorization(
  orgId: string,
  wallet: string,
): Promise<OrganizationAuthorization | null> {
  const id = String(orgId ?? '').trim().toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(id) || !wallet) return null;
  return coalesce(`org:${id}:${wallet}`, () =>
    get<OrganizationAuthorization>(`/organization/${id}/${wallet}`));
}
